import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/api";
import { getOrCreateFlow, patchFlow, type FlowPatch } from "@/features/oic/api";
import type { OICFlow, OICStep } from "@/features/oic/types";

interface State {
  flow: OICFlow | null;
  loading: boolean;
  error: string | null;
}

/**
 * Loads (creating on first access, idempotently) the OIC flow for a case and
 * exposes progress + terms mutators. The wizard routes on the `:step` URL param,
 * so this hook persists progress but never owns the active step.
 */
export function useOicFlow(caseId: string) {
  const [state, setState] = useState<State>({ flow: null, loading: true, error: null });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    getOrCreateFlow(caseId)
      .then((flow) => {
        if (mounted.current) setState({ flow, loading: false, error: null });
      })
      .catch((err) => {
        if (!mounted.current) return;
        setState({
          flow: null,
          loading: false,
          error: err instanceof ApiError ? err.message : "Couldn't start the offer flow.",
        });
      });
    return () => {
      mounted.current = false;
    };
  }, [caseId]);

  const setFlow = useCallback((flow: OICFlow) => {
    if (mounted.current) setState((s) => ({ ...s, flow }));
  }, []);

  /** Persist the active step as the resume target (best-effort). */
  const persistStep = useCallback(
    async (step: OICStep) => {
      const flow = state.flow;
      if (!flow || flow.step === step) return;
      try {
        setFlow(await patchFlow(caseId, { step }));
      } catch {
        // Progress is a resume convenience — a failed persist shouldn't block nav.
      }
    },
    [caseId, state.flow, setFlow],
  );

  const patch = useCallback(
    async (values: FlowPatch) => {
      const updated = await patchFlow(caseId, values);
      setFlow(updated);
      return updated;
    },
    [caseId, setFlow],
  );

  return {
    flow: state.flow,
    loading: state.loading,
    error: state.error,
    setFlow,
    persistStep,
    patch,
  };
}
